import React, { FC, useEffect, useState } from 'react';
import { Button, Input, BaseModal, ToastMessage } from '@cokitchen/cokitchen-components';
import { apiService } from 'utils/api';
import { toast } from 'react-toastify';
import { CoKitchenType } from 'models/cokitchen.model';
import useDashboardContext from 'contexts/dashboard.context';

type ScreenSettingsFormType = { [key: string]: string };

type ScreenSettingsType = {
  show: boolean;
  onClose: () => void;
};

type ScreenInputType = {
  name: string;
  value: string;
  label: string;
};

type SplitScreenType = {
  [key: string]: string;
};

const baseUrl = process.env.REACT_APP_ORDER_STATS_API_KEY;

const ScreenSettingsModal: FC<ScreenSettingsType> = ({ show, onClose }) => {
  const { activeCokitchen } = useDashboardContext();
  const cokitchenId = activeCokitchen.id;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({} satisfies ScreenSettingsFormType);
  const [screenList, setScreenList] = useState([] as ScreenInputType[]);
  const [cokitchens, setCokitchens] = useState([] as CoKitchenType[]);

  const fetchCokitchen = async () => {
    if (cokitchens?.length > 0) return;
    setLoading(true);
    const res = await apiService.get('internal/cokitchens', {
      isInternal: true,
      baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL
    });
    setCokitchens(res.data.data);
    setLoading(false);
  };

  const getScreenList = (data: SplitScreenType) => {
    if (data === undefined) return [];
    const screenList = Object?.entries(data)?.map(([key, value], i) => {
      return {
        name: key,
        value,
        label: `Screen ${i + 1}`
      };
    });
    return screenList;
  };

  const filterCokitchen = () => {
    const filteredCokitchen: CoKitchenType | undefined = cokitchens?.find(
      (cokitchen) => cokitchen.id === cokitchenId
    );
    setFormData(filteredCokitchen?.split_screen_firestore_details as SplitScreenType);
    setScreenList(
      getScreenList(filteredCokitchen?.split_screen_firestore_details as SplitScreenType)
    );
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  useEffect(() => {
    fetchCokitchen();
  }, []);

  useEffect(() => {
    filterCokitchen();
  }, [cokitchens]);

  const updateData = async () => {
    setLoading(true);
    try {
      const data = {
        split_screen_firestore_details: { ...formData },
        cokitchen_id: cokitchenId
      };
      const res = await apiService.put('marketing/cokitchen', { data, baseUrl });
      if (res.status === 200) {
        toast(<ToastMessage type='success' message='Settings Updated successfully!' />);
        onClose();
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      position='right'
      isOpen={show}
      onRequestClose={onClose}
      title='Screen Settings'
      footer={
        <Button color='primary' variant='block' small loading={loading} onClick={updateData}>
          Update
        </Button>
      }>
      <div className='space-y-2'>
        {loading ? (
          <div className='h-[45px] place-content-center '>Loading...</div>
        ) : (
          screenList?.map((screen, index) => (
            <Input
              key={index}
              inputClass='h-[45px] placeholder:text-sm text-sm'
              label={screen.label}
              onChange={handleChange}
              value={Object?.values(formData)[index] as string}
              name={screen?.name}
              placeholder={screen.label}
              disabled={loading}
              type='number'
            />
          ))
        )}
      </div>
    </BaseModal>
  );
};

export default ScreenSettingsModal;
